"use client";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import {
  loggerLink,
  TRPCClientError,
  httpBatchStreamLink as unstable_httpBatchStreamLink,
} from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import Link from "next/link";
import { Button } from "~/components/ui/button";

import { useAuth } from "@clerk/nextjs";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import SuperJSON from "superjson";

import { type AppRouter } from "~/server/api/root";
import { getTrpcErrorMessage } from "~/lib/marketplace/marketplace-hire-flow";

const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        // Disable automatic refetching on window focus by default
        // Individual queries can override this if needed
        refetchOnWindowFocus: false,
        // Don't retry on 401/403 errors (authentication issues)
        retry: (failureCount, error) => {
          if (error instanceof TRPCClientError) {
            // Don't retry auth errors
            if (error.message.includes('User not logged in') || 
                error.message.includes('Unauthorized') ||
                error.message.includes('UNAUTHORIZED')) {
              return false;
            }
          }
          return failureCount < 3;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        // Only show toast for non-auth errors
        if (error instanceof TRPCClientError) {
          if (!error.message.includes('User not logged in') && 
              !error.message.includes('Unauthorized') &&
              !error.message.includes('UNAUTHORIZED')) {
            console.log({ error });
            toast.error(`Error occured: ${getTrpcErrorMessage(error, error.message)}`);
          }
        } else {
          console.log({ error });
          toast.error(`Error occured: ${error.message}`);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error,variables) => {
        interface Args {
          workspaceType: string;
        }
        const args = variables as Args;
        let workspaceType: unknown = false;
        if (args?.workspaceType)
          workspaceType = args.workspaceType;
        if (error instanceof TRPCClientError) {
          // Auth desync: Clerk UI can still look signed-in while API has no session.
          if (
            error.message.includes("User not logged in") ||
            error.message.includes("Unauthorized") ||
            error.message.includes("UNAUTHORIZED")
          ) {
            return;
          }
          if (!workspaceType) {
            toast.error(
              `Error occured: ${getTrpcErrorMessage(error, error.message)}`,
            );
          } else if (workspaceType === "ORGANIZATION") {
            toast.error(
              <div>
                <div>Error occured: {error.message}</div>
                <Button variant="outline">
                  <Link href="/settings/organizations">Show organizations</Link>
                </Button>
              </div>,
            );
          } else if (workspaceType === "PERSONAL") {
            toast.error(
              <div>
                <div>Error occured: {error.message}</div>
                <div className="mt-2 text-sm text-muted-foreground">
                  Please select an organization from the dropdown menu.
                </div>
              </div>,
            );
          }
        }
      },
    }),
  });

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  }
  // Browser: use singleton pattern to keep the same query client
  return (clientQueryClientSingleton ??= createQueryClient());
};

export const api = createTRPCReact<AppRouter>();

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const authRef = useRef({ getToken, isLoaded, isSignedIn });
  useEffect(() => {
    authRef.current = { getToken, isLoaded, isSignedIn };
  }, [getToken, isLoaded, isSignedIn]);

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === "development" ||
            (op.direction === "down" && op.result instanceof Error),
        }),
        unstable_httpBatchStreamLink({
          transformer: SuperJSON,
          url: getBaseUrl() + "/api/trpc",
          headers: async () => {
            const headers = new Headers();
            headers.set("x-trpc-source", "nextjs-react");
            // Never block public marketing queries (e.g. siteglue.ai Live AI employees)
            // on Clerk boot. Only attach a Bearer token when the session is ready.
            const { getToken: gt, isLoaded: loaded, isSignedIn: signedIn } =
              authRef.current;
            if (loaded && signedIn) {
              try {
                const token = await Promise.race([
                  Promise.resolve(gt()).then((t) => t ?? null),
                  new Promise<null>((resolve) =>
                    setTimeout(() => resolve(null), 2500),
                  ),
                ]);
                if (token) headers.set("Authorization", `Bearer ${token}`);
              } catch {
                // cookies may still authenticate same-origin requests
              }
            }
            return headers;
          },
          fetch(url, options) {
            return fetch(url, {
              ...options,
              credentials: "include",
            });
          },
          maxURLLength: 2048,
          maxBatchSize: 10,
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </api.Provider>
    </QueryClientProvider>
  );
}

function getBaseUrl() {
  // CRITICAL: Always use window.location.origin on client side for multi-domain support
  // This ensures the correct domain is used regardless of which brand/domain is loaded
  if (typeof window !== "undefined") return window.location.origin;
  
  // Server-side: use env vars
  if (process.env.NEXT_PUBLIC_DEPLOYMENT_URL) return `https://${process.env.NEXT_PUBLIC_DEPLOYMENT_URL}`;
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
  return `http://localhost:${process.env.PORT ?? 3000}`;
}
